import type {
  HandleEscalationInput,
  HandleEscalationResult,
  MyRoutingGroup,
  RoutingGroupEventWithTicketState,
  RoutingGroupInterface,
  RoutingPreviewResult
} from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

// LINK apps/server/src/API/RoutingGroup/RoutingGroup.Controller.ts
export class RoutingGroupAPI {
  // ─── Groups ──────────────────────────────────────────────────────────────

  static async listGroups(orgId: number): Promise<RoutingGroupInterface[]> {
    return APIService.Server.get<RoutingGroupInterface[]>(`/routing/org/${orgId}/groups`);
  }

  /** Same group data as `listGroups`, gated server-side on the `ticketing_groups` flag — notification-preferences picker only. */
  static async listGroupsForNotificationPreferences(orgId: number): Promise<RoutingGroupInterface[]> {
    return APIService.Server.get<RoutingGroupInterface[]>(`/routing/org/${orgId}/groups/for-notification-preferences`);
  }

  /** Groups the signed-in member belongs to, each with its unaccepted-ticket count (Group Inbox nav rail). */
  static async listMyGroups(orgId: number): Promise<MyRoutingGroup[]> {
    return APIService.Server.get<MyRoutingGroup[]>(`/routing/org/${orgId}/groups/mine`);
  }

  /**
   * Create or update a routing group (Phase II shape). The group's
   * `topics` + `roleSlugs` JSONB lists travel in the body. Multi-claim
   * is allowed at the same scope — the routing resolver picks the
   * most-recently-updated claimant as the primary; the PM UI shows
   * non-primary claimants struck through.
   */
  static async upsertGroup(orgId: number, body: Partial<RoutingGroupInterface>): Promise<RoutingGroupInterface> {
    return APIService.Server.post<RoutingGroupInterface>(`/routing/org/${orgId}/group`, body);
  }

  static async deleteGroup(orgId: number, groupId: number): Promise<{ ok: true }> {
    return APIService.Server.delete(`/routing/org/${orgId}/group/${groupId}`);
  }

  // ─── Preview / events / members ──────────────────────────────────────────

  static async preview(orgId: number, params: { locationId?: number; routingTopic?: string }): Promise<RoutingPreviewResult> {
    return APIService.Server.get<RoutingPreviewResult>(`/routing/org/${orgId}/preview`, params);
  }

  /** ENG-3089 — same resolution as `preview`, internal auth, for the bot's sandbox contact gate. */
  static async previewInternal(orgId: number, params: { locationId?: number; routingTopic?: string }): Promise<RoutingPreviewResult> {
    return APIService.Server.get<RoutingPreviewResult>(`/routing/org/${orgId}/preview-internal`, params);
  }

  static async listEvents(orgId: number, params: { locationId?: number; limit?: number }): Promise<RoutingGroupEventWithTicketState[]> {
    return APIService.Server.get<RoutingGroupEventWithTicketState[]>(`/routing/org/${orgId}/events`, params);
  }

  static async listGroupMembers(orgId: number, slug: string, params: { locationId?: number }): Promise<{ memberIds: number[] }> {
    return APIService.Server.get<{ memberIds: number[] }>(`/routing/org/${orgId}/group/${slug}/members`, params);
  }

  // ─── Centralized escalation handler ────────────────────────────────────────

  /**
   * POSTs to the centralized escalation handler. Bot tools call this with
   * their captured ticket content + routingTopic and branch on the returned
   * outcome to compose the customer-facing reply.
   */
  static async handleEscalation(orgId: number, input: Omit<HandleEscalationInput, "orgId">): Promise<HandleEscalationResult> {
    return APIService.Server.post<HandleEscalationResult>(`/routing/org/${orgId}/handle-escalation`, input);
  }
}
